import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from 'src/common/utils/api';
import AuthRequestService from './AuthRequestService';
import { Container, Title, Button, Group, Text, Center, Stack } from '@mantine/core';
import PageLoader from '../../UI/loader/PageLoader';

type Job = {
  id: string;
  status: string;
  statusMessage: string;
};

type ConnectionPayload = {
  code: string | null;
  state: string | null;
  idToken: string | null;
  authRequestId: string;
};

function useDebouncedEffect(delay: number, effect: () => void) {
  useEffect(() => {
    const timeout = setTimeout(effect, delay);
    return () => clearTimeout(timeout);
  }, []);
}

export const CompleteConnectionFlowPage = () => {
  const navigate = useNavigate();
  const [allowPolling, setAllowPolling] = useState(true);

  const {
    mutate,
    data: createConnectionResponse,
    isError,
  } = useMutation<{ data: Job }, Error, ConnectionPayload>({
    mutationFn: values => api.post<Job>('connections', values),
  });

  const jobId = createConnectionResponse?.data?.id;

  const { data: job } = useQuery<Job>({
    queryKey: ['connections-jobs', jobId],
    enabled: !!jobId && allowPolling,
    refetchInterval: 2000,
  });

  async function completeConnectionFlow() {
    const u = new URL(window.location.href);
    const code = u.searchParams.get('code');
    const state = u.searchParams.get('state');
    const idToken = u.searchParams.get('id_token');
    const authRequestId = AuthRequestService.getAuthRequestId();
    mutate({ code, state, idToken, authRequestId });
  }

  useDebouncedEffect(300, () => {
    completeConnectionFlow();
  });

  useEffect(() => {
    if (job?.status === 'SUCCESS') {
      setAllowPolling(false);
      navigate('/');
    }
    if (job?.status === 'FAILED') {
      setAllowPolling(false);
    }
  }, [job, navigate]);

  return job?.status === 'FAILED' || isError ? (
    <Container>
      <Title ta="center" mt={'xxl'}>
        Provider connection failed...
      </Title>
      <Text c="dimmed" size="lg" ta="center" mt={'xs'}>
        We have encountered an error while connecting to the provider. Please go back and try again.
      </Text>
      <Group justify="center" mt={'xxl'}>
        <Button onClick={() => navigate('/')}>Back</Button>
      </Group>
    </Container>
  ) : (
    <Center>
      <Stack>
        <PageLoader />
        <Text>{job?.statusMessage || 'configuring...'} </Text>
      </Stack>
    </Center>
  );
};
